// ─── CoachTab – regelbasierter Übungscoach ────────────────────────────────────
import { useState, useEffect, useCallback, useRef } from "react";
import { getCoachToday, refreshCoach, coachAction, getCoachPrefs, saveCoachPrefs, coachChat } from "./api";

// ─── Design Tokens (gespiegelt aus App.jsx) ──────────────────────────────────
const T = {
  bg:           '#FCFCFA',
  surface:      '#FFFFFF',
  surfaceSoft:  '#F6F6F3',
  border:       '#E8E8E2',
  text:         '#171717',
  textSoft:     '#6B6B66',
  textFaint:    '#A1A19A',
  accent:       '#6FAE8D',
  accentStrong: '#4F8C6B',
  accentSoft:   '#E7F3EC',
  rc:           22,
  rp:           999,
  ri:           16,
  shadow:       '0 10px 30px rgba(0,0,0,0.04)',
};

const DS = {
  card:  { borderRadius: T.rc, background: T.surface, border: `1px solid ${T.border}`, boxShadow: T.shadow, padding: 16 },
  soft:  { borderRadius: T.rc, background: T.surfaceSoft, border: `1px solid ${T.border}`, padding: 16 },
  pill:  { borderRadius: T.rp, border: 'none', cursor: 'pointer', fontFamily: 'Inter,sans-serif', fontSize: 12, fontWeight: 600, padding: '6px 14px' },
  pillSm:{ borderRadius: T.rp, border: 'none', cursor: 'pointer', fontFamily: 'Inter,sans-serif', fontSize: 11, fontWeight: 600, padding: '4px 10px' },
  inp:   { borderRadius: T.ri, border: `1px solid ${T.border}`, padding: '10px 14px', fontFamily: 'Inter,sans-serif', fontSize: 14, outline: 'none', background: T.surface, width: '100%', boxSizing: 'border-box' },
  label: { fontSize: 11, fontWeight: 600, color: T.textFaint, letterSpacing: '0.06em', fontFamily: 'Inter,sans-serif', textTransform: 'uppercase' },
  body:  { fontSize: 14, color: T.textSoft, fontFamily: 'Inter,sans-serif', lineHeight: '22px' },
  small: { fontSize: 12, color: T.textSoft, fontFamily: 'Inter,sans-serif', lineHeight: '18px', fontWeight: 500 },
};

// ─── Type icons & colors ─────────────────────────────────────────────────────
const TYPE_META = {
  scale:   { icon: "♩", label: "Tonleiter", color: "#6FAE8D", bg: "#E7F3EC" },
  cadence: { icon: "♫", label: "Kadenz",    color: "#7B97C5", bg: "#EEF4FA" },
  piece:   { icon: "♪", label: "Stück",     color: "#C5975A", bg: "#FBF3E8" },
  theory:  { icon: "◎", label: "Theorie",   color: "#A07BC5", bg: "#F3EEFB" },
};

function typeColor(type) { return TYPE_META[type]?.color || T.accentStrong; }
function typeBg(type)    { return TYPE_META[type]?.bg    || T.accentSoft; }
function typeIcon(type)  { return TYPE_META[type]?.icon  || "♩"; }
function typeLabel(type) { return TYPE_META[type]?.label || type; }

// ─── Level / Focus labels ────────────────────────────────────────────────────
const LEVELS  = [
  { id: "beginner",     label: "Einsteiger" },
  { id: "intermediate", label: "Fortgeschritten" },
  { id: "advanced",     label: "Fortgeschritten+" },
];
const FOCUSES = [
  { id: "mixed",      label: "Gemischt" },
  { id: "theory",     label: "Theorie" },
  { id: "improv",     label: "Improvisation" },
  { id: "repertoire", label: "Repertoire" },
  { id: "harmony",    label: "Harmonielehre" },
];
const MINUTE_OPTIONS = [10, 15, 20, 30, 45, 60];

// ─── Greeting ────────────────────────────────────────────────────────────────
function greeting(name) {
  const h = new Date().getHours();
  const g = h < 12 ? "Guten Morgen" : h < 18 ? "Hallo" : "Guten Abend";
  return `${g}, ${name}`;
}

// ─── PlanItem ────────────────────────────────────────────────────────────────
function PlanItem({ item, index, onAction, setTab }) {
  function handleAction() {
    if (item.type === "scale" || item.type === "cadence") {
      onAction("started");
      setTab("scale");
    } else if (item.type === "piece") {
      onAction("started");
      setTab("progress");
    } else if (item.type === "theory") {
      onAction("started");
      setTab("theory");
    }
  }

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "12px 14px", borderRadius: T.ri,
      background: T.surface, border: `1px solid ${T.border}`,
    }}>
      {/* Type badge */}
      <div style={{
        width: 38, height: 38, borderRadius: 12, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: typeBg(item.type), fontSize: 18,
      }}>{typeIcon(item.type)}</div>

      {/* Label */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Inter,sans-serif", fontSize: 13, fontWeight: 600, color: T.text }}>{item.label}</div>
        <div style={{ ...DS.small, color: T.textFaint, marginTop: 1 }}>
          {typeLabel(item.type)} · {item.minutes} Min
        </div>
      </div>

      {/* Action button */}
      <button onClick={handleAction} style={{
        ...DS.pillSm, flexShrink: 0,
        background: typeColor(item.type), color: "#fff",
      }}>
        Starten →
      </button>
    </div>
  );
}

// ─── Preferences Panel ───────────────────────────────────────────────────────
function PrefsPanel({ prefs, onSave }) {
  const [level,   setLevel]   = useState(prefs.level    || "beginner");
  const [mins,    setMins]    = useState(prefs.daily_minutes || 20);
  const [focus,   setFocus]   = useState(prefs.focus    || "mixed");
  const [saving,  setSaving]  = useState(false);

  async function save() {
    setSaving(true);
    try {
      const saved = await saveCoachPrefs({ level, daily_minutes: mins, focus });
      onSave(saved);
    } catch {}
    finally { setSaving(false); }
  }

  const segStyle = (active) => ({
    ...DS.pillSm, flex: 1, padding: "6px 4px", textAlign: "center",
    background: active ? T.text : "transparent",
    color: active ? "#fff" : T.textSoft,
    border: `1px solid ${active ? T.text : T.border}`,
    borderRadius: 10,
  });

  return (
    <div style={{ ...DS.soft, display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ ...DS.label, color: T.textFaint }}>Coach-Einstellungen</div>

      {/* Daily minutes */}
      <div>
        <div style={{ ...DS.small, color: T.textSoft, marginBottom: 8 }}>Übungszeit pro Tag</div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {MINUTE_OPTIONS.map(m => (
            <button key={m} onClick={() => setMins(m)} style={{
              ...DS.pillSm,
              background: mins === m ? T.text : T.surface,
              color: mins === m ? "#fff" : T.textSoft,
              border: `1px solid ${mins === m ? T.text : T.border}`,
            }}>{m} Min</button>
          ))}
        </div>
      </div>

      {/* Level */}
      <div>
        <div style={{ ...DS.small, color: T.textSoft, marginBottom: 8 }}>Niveau</div>
        <div style={{ display: "flex", gap: 5 }}>
          {LEVELS.map(l => (
            <button key={l.id} onClick={() => setLevel(l.id)} style={segStyle(level === l.id)}>{l.label}</button>
          ))}
        </div>
      </div>

      {/* Focus */}
      <div>
        <div style={{ ...DS.small, color: T.textSoft, marginBottom: 8 }}>Fokus</div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {FOCUSES.map(f => (
            <button key={f.id} onClick={() => setFocus(f.id)} style={{
              ...DS.pillSm,
              background: focus === f.id ? T.accent : T.surface,
              color: focus === f.id ? "#fff" : T.textSoft,
              border: `1px solid ${focus === f.id ? T.accent : T.border}`,
            }}>{f.label}</button>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} style={{
        ...DS.pill, background: T.accent, color: "#fff", opacity: saving ? 0.6 : 1,
      }}>{saving ? "Speichern…" : "Einstellungen speichern"}</button>
    </div>
  );
}

// ─── AiCoachChat ─────────────────────────────────────────────────────────────
function AiCoachChat() {
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [open,     setOpen]     = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  async function send() {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: "user", content: text };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const { reply } = await coachChat(text, messages);
      setMessages([...next, { role: "assistant", content: reply }]);
    } catch (e) {
      setError(e.message || "Verbindungsfehler");
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  }

  const STARTER_QUESTIONS = [
    "Was soll ich heute üben?",
    "Erkläre mir den II–V–I",
    "Wie verbessere ich mein Timing?",
  ];

  return (
    <div style={{ ...DS.card, padding: 0, overflow: "hidden" }}>
      {/* Header / Toggle */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", background: "none", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 10, padding: "14px 16px",
        }}
      >
        <div style={{
          width: 32, height: 32, borderRadius: 10, flexShrink: 0,
          background: "linear-gradient(135deg, #2D3B2D, #4F8C6B)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 16, color: "#fff",
        }}>✦</div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={{ fontFamily: "Inter,sans-serif", fontSize: 13, fontWeight: 700, color: T.text }}>KI-Coach</div>
          <div style={{ ...DS.small, color: T.textFaint, marginTop: 1 }}>Stell mir eine Frage</div>
        </div>
        <div style={{ fontSize: 13, color: T.textFaint, fontFamily: "Inter,sans-serif" }}>{open ? "−" : "+"}</div>
      </button>

      {open && (
        <div style={{ borderTop: `1px solid ${T.border}` }}>
          {/* Starter-Fragen (nur wenn kein Verlauf) */}
          {messages.length === 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "10px 14px 4px" }}>
              {STARTER_QUESTIONS.map(q => (
                <button key={q} onClick={() => { setInput(q); }} style={{
                  ...DS.pillSm, background: T.surfaceSoft, color: T.textSoft,
                  border: `1px solid ${T.border}`, fontSize: 11,
                }}>{q}</button>
              ))}
            </div>
          )}

          {/* Nachrichtenverlauf */}
          {messages.length > 0 && (
            <div style={{ maxHeight: 280, overflowY: "auto", padding: "10px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
              {messages.map((m, i) => (
                <div key={i} style={{
                  display: "flex",
                  justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                }}>
                  <div style={{
                    maxWidth: "82%",
                    padding: "8px 12px",
                    borderRadius: m.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                    background: m.role === "user" ? T.text : T.accentSoft,
                    color: m.role === "user" ? "#fff" : T.text,
                    fontFamily: "Inter,sans-serif", fontSize: 13, lineHeight: "20px",
                    whiteSpace: "pre-wrap",
                  }}>{m.content}</div>
                </div>
              ))}
              {loading && (
                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                  <div style={{
                    padding: "8px 14px", borderRadius: "14px 14px 14px 4px",
                    background: T.accentSoft, color: T.textFaint,
                    fontFamily: "Inter,sans-serif", fontSize: 13,
                    display: "flex", gap: 4, alignItems: "center",
                  }}>
                    <span style={{ animation: "pulse 1s infinite" }}>●</span>
                    <span style={{ opacity: 0.5 }}>●</span>
                    <span style={{ opacity: 0.3 }}>●</span>
                  </div>
                </div>
              )}
              {error && (
                <div style={{ ...DS.small, color: "#C0392B", textAlign: "center" }}>{error}</div>
              )}
              <div ref={endRef} />
            </div>
          )}

          {/* Eingabe */}
          <div style={{ display: "flex", gap: 8, padding: "10px 14px 14px", alignItems: "flex-end" }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Frag deinen Coach…"
              rows={1}
              style={{
                ...DS.inp, flex: 1, resize: "none", lineHeight: "20px",
                minHeight: 38, maxHeight: 100, overflowY: "auto",
              }}
            />
            <button
              onClick={send}
              disabled={!input.trim() || loading}
              style={{
                ...DS.pill, background: T.accent, color: "#fff",
                padding: "9px 14px", flexShrink: 0,
                opacity: (!input.trim() || loading) ? 0.5 : 1,
              }}
            >↑</button>
          </div>

          {messages.length > 0 && (
            <div style={{ textAlign: "right", padding: "0 14px 10px" }}>
              <button onClick={() => setMessages([])} style={{
                background: "none", border: "none", cursor: "pointer",
                ...DS.small, color: T.textFaint, fontSize: 11,
              }}>Gespräch löschen</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main CoachTab ────────────────────────────────────────────────────────────
export default function CoachTab({ user, setTab }) {
  const [rec,       setRec]       = useState(null);
  const [prefs,     setPrefs]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [refreshing,setRefreshing]= useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [showWhy,   setShowWhy]   = useState(false);

  const name = (user.display_name || user.username).split(" ")[0];

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getCoachToday(), getCoachPrefs()])
      .then(([r, p]) => { setRec(r); setPrefs(p); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      const r = await refreshCoach();
      setRec(r);
    } catch {}
    finally { setRefreshing(false); }
  }

  async function handleAction(status) {
    try {
      await coachAction(status);
      setRec(prev => prev ? { ...prev, status } : prev);
    } catch {}
  }

  function handleSavePrefs(saved) {
    setPrefs(saved);
    setShowPrefs(false);
    // Regenerate recommendation with new prefs
    handleRefresh();
  }

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 0" }}>
        <div style={{ ...DS.small, color: T.textFaint }}>Coach denkt nach…</div>
      </div>
    );
  }

  const status   = rec?.status || "new";
  const isDone   = status === "done";
  const isDismissed = status === "dismissed";
  const plan     = rec?.plan     || [];
  const reasons  = rec?.reasons  || [];
  const streak   = rec?.streak   || 0;
  const totalMins = rec?.totalMins || plan.reduce((s, p) => s + p.minutes, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 16 }}>

      {/* ── Hero Card ── */}
      <div style={{
        borderRadius: T.rc, padding: "20px 18px",
        background: "linear-gradient(135deg, #1E2A1E 0%, #2D3B2D 100%)",
        color: "#fff", position: "relative", overflow: "hidden",
      }}>
        {/* Background decoration */}
        <div style={{
          position: "absolute", right: -20, top: -20,
          width: 120, height: 120, borderRadius: "50%",
          background: "rgba(111,174,141,0.12)",
        }} />
        <div style={{
          position: "absolute", right: 20, bottom: -30,
          width: 80, height: 80, borderRadius: "50%",
          background: "rgba(111,174,141,0.08)",
        }} />

        <div style={{ ...DS.label, color: "rgba(255,255,255,0.45)", marginBottom: 8 }}>
          Dein Coach · {new Date().toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })}
        </div>
        <div style={{
          fontFamily: "'Instrument Serif',Georgia,serif",
          fontSize: 24, fontStyle: "italic", color: "#fff",
          marginBottom: 4, lineHeight: "28px",
        }}>{greeting(name)}</div>

        {rec && (
          <>
            <div style={{ fontFamily: "Inter,sans-serif", fontSize: 16, fontWeight: 700, color: "#fff", marginTop: 12, marginBottom: 4 }}>
              {rec.title}
            </div>
            <div style={{ fontFamily: "Inter,sans-serif", fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
              {rec.summary}
            </div>
          </>
        )}

        {/* Streak badge */}
        {streak > 0 && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            marginTop: 14, padding: "5px 12px", borderRadius: T.rp,
            background: "rgba(111,174,141,0.25)", border: "1px solid rgba(111,174,141,0.4)",
          }}>
            <span style={{ fontSize: 12 }}>●</span>
            <span style={{ fontFamily: "Inter,sans-serif", fontSize: 11, fontWeight: 700, color: T.accent }}>
              {streak} {streak === 1 ? "Tag" : "Tage"} Streak
            </span>
          </div>
        )}

        {/* Done overlay */}
        {isDone && (
          <div style={{
            position: "absolute", inset: 0, borderRadius: T.rc,
            background: "rgba(30,42,30,0.85)", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 6 }}>✓</div>
              <div style={{ fontFamily: "Inter,sans-serif", fontSize: 14, fontWeight: 700, color: T.accent }}>Session erledigt!</div>
              <div style={{ ...DS.small, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>Morgen geht's weiter.</div>
            </div>
          </div>
        )}
      </div>

      {/* ── Status-Aktionen ── */}
      {!isDone && !isDismissed && (
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => handleAction("done")} style={{
            ...DS.pill, flex: 1, background: T.accent, color: "#fff", padding: "10px",
          }}>
            ✓ Als erledigt markieren
          </button>
          <button onClick={handleRefresh} disabled={refreshing} style={{
            ...DS.pillSm, background: T.surfaceSoft, color: T.textSoft,
            border: `1px solid ${T.border}`, padding: "10px 14px",
            opacity: refreshing ? 0.5 : 1,
          }}>
            {refreshing ? "…" : "↺"}
          </button>
        </div>
      )}

      {isDone && (
        <button onClick={() => { handleAction("new"); handleRefresh(); }} style={{
          ...DS.pill, background: T.surfaceSoft, color: T.textSoft,
          border: `1px solid ${T.border}`, width: "100%",
        }}>
          Neue Session starten
        </button>
      )}

      {/* ── Tagesplan ── */}
      {plan.length > 0 && !isDone && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ ...DS.label, color: T.textFaint }}>Tagesplan · {totalMins} Minuten</div>
          {plan.map((item, i) => (
            <PlanItem key={i} item={item} index={i} onAction={handleAction} setTab={setTab} />
          ))}
        </div>
      )}

      {/* ── Schnellstart-Filter ── */}
      {!isDone && plan.length > 0 && (
        <div>
          <div style={{ ...DS.label, color: T.textFaint, marginBottom: 8 }}>Schnell anpassen</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              { label: "Nur 10 Min", action: () => { saveCoachPrefs({ daily_minutes: 10 }).then(() => handleRefresh()); } },
              { label: "Mehr Theorie", action: () => { saveCoachPrefs({ focus: "theory" }).then(() => handleRefresh()); } },
              { label: "Mehr Stücke", action: () => { saveCoachPrefs({ focus: "repertoire" }).then(() => handleRefresh()); } },
            ].map(btn => (
              <button key={btn.label} onClick={btn.action} style={{
                ...DS.pillSm, background: T.surfaceSoft, color: T.textSoft,
                border: `1px solid ${T.border}`,
              }}>{btn.label}</button>
            ))}
          </div>
        </div>
      )}

      {/* ── Warum diese Empfehlung? ── */}
      {reasons.length > 0 && (
        <div style={{ ...DS.soft }}>
          <button
            onClick={() => setShowWhy(w => !w)}
            style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, width: "100%", padding: 0 }}
          >
            <div style={{ ...DS.label, color: T.textFaint, flex: 1, textAlign: "left" }}>Warum diese Empfehlung?</div>
            <div style={{ fontSize: 14, color: T.textFaint }}>{showWhy ? "−" : "+"}</div>
          </button>
          {showWhy && (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {reasons.map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                    background: T.accentSoft, border: `1px solid ${T.accent}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "Inter,sans-serif", fontSize: 10, fontWeight: 700, color: T.accentStrong,
                  }}>{i + 1}</div>
                  <div style={{ ...DS.body, fontSize: 13, paddingTop: 1 }}>{r}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── KI-Coach Chat ── */}
      <AiCoachChat />

      {/* ── Einstellungen ── */}
      <button
        onClick={() => setShowPrefs(s => !s)}
        style={{
          background: "none", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6, padding: "8px 0",
          fontFamily: "Inter,sans-serif", fontSize: 12, fontWeight: 600, color: T.textFaint,
        }}
      >
        <span style={{ fontSize: 14 }}>{showPrefs ? "▲" : "▼"}</span>
        {prefs ? `${prefs.daily_minutes} Min · ${LEVELS.find(l => l.id === prefs.level)?.label || ""} · ${FOCUSES.find(f => f.id === prefs.focus)?.label || ""}` : "Coach-Einstellungen"}
      </button>

      {showPrefs && prefs && (
        <PrefsPanel prefs={prefs} onSave={handleSavePrefs} />
      )}
    </div>
  );
}
